import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useEffect, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Welcome from '../../components/Header/Welcome';
import { AdminNav } from '../../components';

const Countries = () => {
  const [countries, setCountries] = useState([]);

  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [newNameCountry, setNewNameCountry] = useState('');
  const [newAbbreviationCountry, setNewAbbreviationCountry] = useState('');
  const [idToUpdate, setIdToUpdate] = useState('');

  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getAllCountries = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/countries`
      );
      setCountries(response.data.countries);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCountry = async () => {
    if (newNameCountry.trim() === '') {
      return alert(lang === 'es' ? 'Debe ingresar un nombre de país' : 'Please enter country name');
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/countries`,
        { name: newNameCountry, abbreviation: newAbbreviationCountry },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNewNameCountry('');
      setNewAbbreviationCountry('');
      setIdToUpdate('');
      getAllCountries();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteCountry = async id => {
    const confirm = window.confirm(texts.admin_common.delete_confirm);
    if (!confirm) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/countries/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      getAllCountries();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditCountry = async id => {
    setIsUpdate(true);
    setIdToUpdate(id);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/countries/${id}`
      );
      setNewNameCountry(response.data.country.name);
      setNewAbbreviationCountry(response.data.country.abbreviation);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateCountry = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/countries/${idToUpdate}`,
        { name: newNameCountry, abbreviation: newAbbreviationCountry },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNewNameCountry('');
      setNewAbbreviationCountry('');
      setIdToUpdate('');
      setIsUpdate(false);
      getAllCountries();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (token) {
      getAllCountries();
    }
  }, [token]);

  if (!currentUser?.isAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <>
      <Welcome />

      <div className="container py-4" style={{ minHeight: '80vh' }}>
        <AdminNav />

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-globe"></i>
              <span>
                {texts.admin_common.list_of} {texts.admin.menu_countries}
              </span>
            </h2>
            <span className="text-muted small">
              {lang === 'es'
                ? 'Administra los países de origen y destino disponibles en la plataforma.'
                : 'Manage origin and destination countries supported by the platform.'}
            </span>
          </div>
        </div>

        <div className="row g-4">
          {/* Form Card */}
          <div className="col-12 col-lg-4">
            <div
              className="card border-0 shadow-sm rounded-4 bg-white sticky-top"
              style={{ top: '20px' }}
            >
              <div className="card-body p-4">
                <h5 className="fw-bold text-sen mb-3 d-flex align-items-center gap-2">
                  <i className={isUpdate ? 'fa fa-edit' : 'fa fa-plus-circle'}></i>
                  <span>
                    {isUpdate ? texts.admin_common.update : texts.admin_common.save}{' '}
                    {texts.admin.menu_countries}
                  </span>
                </h5>

                <div className="mb-3">
                  <label htmlFor="country-name" className="form-label small fw-bold text-secondary">
                    {texts.admin_common.name}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-globe"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control bg-light border-start-0"
                      id="country-name"
                      placeholder="Nombre del país (ej: Chile)"
                      onChange={e => setNewNameCountry(e.target.value)}
                      value={newNameCountry}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="country-abbr" className="form-label small fw-bold text-secondary">
                    Abreviatura / Código ISO
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-tag"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control bg-light border-start-0 text-uppercase"
                      id="country-abbr"
                      placeholder="CL"
                      maxLength="4"
                      onChange={e => setNewAbbreviationCountry(e.target.value.toUpperCase())}
                      value={newAbbreviationCountry}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                  {isUpdate && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-3"
                      onClick={() => {
                        setNewNameCountry('');
                        setNewAbbreviationCountry('');
                        setIdToUpdate('');
                        setIsUpdate(false);
                      }}
                    >
                      {texts.admin_common.cancel}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary bg-sen border-0 px-4 fw-bold shadow-sm"
                    onClick={isUpdate ? handleUpdateCountry : handleSaveCountry}
                  >
                    {isUpdate ? texts.admin_common.update : texts.admin_common.save}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="py-3 px-4 text-secondary small fw-bold text-uppercase">
                          {texts.admin_common.name}
                        </th>
                        <th className="py-3 px-3 text-secondary small fw-bold text-uppercase text-center">
                          Abreviatura
                        </th>
                        <th className="py-3 px-4 text-secondary small fw-bold text-uppercase text-end">
                          {texts.admin_common.options}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="3" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Cargando...</span>
                            </div>
                          </td>
                        </tr>
                      ) : countries.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center py-5 text-muted">
                            <i className="fa fa-folder-open-o fs-3 d-block mb-2 text-secondary"></i>
                            No hay países registrados
                          </td>
                        </tr>
                      ) : (
                        countries.map(country => (
                          <tr key={country.id}>
                            <td className="py-3 px-4 fw-semibold text-dark">{country.name}</td>
                            <td className="py-3 px-3 text-center">
                              <span className="badge bg-light text-dark border px-3 py-2 fw-bold">
                                {country.abbreviation}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-end">
                              {country.abbreviation !== 'CL' ? (
                                <>
                                  <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={() => handleEditCountry(country.id)}
                                    title="Editar"
                                  >
                                    <i className="fa fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteCountry(country.id)}
                                    title="Eliminar"
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                </>
                              ) : (
                                <span className="badge bg-secondary opacity-75">
                                  Sistema Principal
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Countries;
